import { arrayValue, intValue, mapValue, stringValue } from "@/utils/coerce";
import type { TraceLane, TraceNode } from "@/utils/trace/model";

/**
 * The lane's own payload as a selectable node.
 *
 * A sub-agent execution holds what it was asked to do and what it concluded on
 * the flow itself, not on any child node, so without this pseudo-node there is
 * no way to reach `request_payload` / `result_payload` at all. Shared with the
 * no-time-axis view, which used to skip it entirely and left those payloads
 * unreachable there.
 */
export function laneNodeFor(lane: TraceLane): TraceNode | null {
  if (!lane.detail) return null;
  const durationMs =
    lane.startMs !== null && lane.endMs !== null ? lane.endMs - lane.startMs : null;
  return {
    id: lane.id,
    // Its own kind so it takes the lane's own colour rather than the blue that
    // means "inference", and so tool grouping leaves it alone.
    kind:
      lane.kind === "sub_agent"
        ? "sa_execution"
        : lane.kind === "tool_batch"
          ? "tool_batch"
          : "turn",
    label: lane.sublabel ? `${lane.label} · ${lane.sublabel}` : lane.label,
    startMs: lane.startMs,
    endMs: lane.endMs,
    status: lane.status ?? "unknown",
    rawStatus: lane.rawStatus ?? "",
    durationMs,
    // The duration is measured; only where it starts is inferred, and that is
    // true exactly when the lane's own nodes were reconstructed.
    approximate: lane.nodes.some((node) => node.approximate),
    detail: lane.detail,
    children: [],
  };
}

/** `ExecutorSummary` — see base_agent app/services/sub_agent_contracts.py. */
export type ExecutorSummary = {
  objective: string;
  successCriteria: string[];
  preferredSkills: string[];
  state: string;
  status: string;
  findings: Array<{ claim: string; evidenceCount: number }>;
  uncertainties: string[];
  recommendedNextActions: string[];
  evidenceCount: number;
};

/** Pull the sub-agent contract out of a flow payload. Empty when absent. */
export function readExecutorSummary(
  detail: Record<string, unknown> | undefined,
): ExecutorSummary | null {
  if (!detail) return null;
  const request = mapValue(detail.request_payload);
  const result = mapValue(detail.result_payload);
  const summary = mapValue(result.summary);
  const objective = stringValue(request.objective, "");
  const findings = arrayValue(summary.findings).map((raw) => {
    const finding = mapValue(raw);
    return {
      claim: stringValue(finding.claim, ""),
      evidenceCount: arrayValue(finding.evidence_ids).length,
    };
  });

  if (!objective && findings.length === 0 && Object.keys(result).length === 0) {
    return null;
  }

  return {
    objective,
    successCriteria: arrayValue(request.success_criteria)
      .map((item) => stringValue(item, ""))
      .filter(Boolean),
    preferredSkills: arrayValue(request.preferred_skills)
      .map((item) => stringValue(item, ""))
      .filter(Boolean),
    state: stringValue(result.state, ""),
    status: stringValue(summary.status, ""),
    findings: findings.filter((finding) => finding.claim),
    uncertainties: arrayValue(summary.uncertainties)
      .map((item) => stringValue(item, ""))
      .filter(Boolean),
    recommendedNextActions: arrayValue(summary.recommended_next_actions)
      .map((item) => stringValue(item, ""))
      .filter(Boolean),
    evidenceCount: arrayValue(result.evidence_refs).length,
  };
}

/**
 * What the sub-agent was asked to do and how it ended, without a click.
 *
 * Everything here was already in the response and already reaching the
 * component — it just sat behind a lane header that looked like a label, so in
 * practice nobody ever saw it.
 */
export function SubAgentInlineSummary({ lane }: { lane: TraceLane }) {
  if (lane.kind !== "sub_agent") return null;
  const summary = readExecutorSummary(lane.detail);
  if (!summary) return null;

  const errorMessage = stringValue(
    mapValue(mapValue(lane.detail).error_payload).message,
    "",
  );
  const roundCount = intValue(arrayValue(mapValue(lane.detail).model_turns).length);

  return (
    <div className="ml-1 grid gap-1 border-l-2 border-trace-subagent/40 py-0.5 pl-2 text-xs">
      {summary.objective && (
        <p className="line-clamp-2 text-muted-foreground">{summary.objective}</p>
      )}
      <div className="flex flex-wrap gap-x-2.5 gap-y-1 text-muted-foreground">
        {summary.state && <span>state {summary.state}</span>}
        {summary.status && <span>summary {summary.status}</span>}
        <span>{summary.findings.length} finding(s)</span>
        {summary.uncertainties.length > 0 && (
          <span>{summary.uncertainties.length} uncertainty(ies)</span>
        )}
        {summary.evidenceCount > 0 && <span>{summary.evidenceCount} evidence</span>}
        {roundCount !== null && roundCount > 0 && <span>{roundCount} round(s)</span>}
      </div>
      {errorMessage && (
        <p className="text-destructive">{errorMessage.slice(0, 200)}</p>
      )}
    </div>
  );
}
